import React, { Component } from 'react';

import PropTypes from 'prop-types';

import CannySDK from 'common/CannySDK';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import { LoadVideoContext, PlayVideoContext } from 'common/containers/WistiaContainer';
import publicConfig from 'common/core/publicConfig';
import LandingChangelogHelmet from 'common/helmets/LandingChangelogHelmet';
import InstallExtensionButton from 'common/integrations/InstallExtensionButton';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Tappable from 'common/Tappable';
import UppercaseHeader from 'common/UppercaseHeader';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import FeaturesChildPagePagination from 'common/www/FeaturesChildPagePagination';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Changelog1 from 'img/landing/changelog-1.webp';
import Changelog2 from 'img/landing/changelog-2.webp';
import Changelog3 from 'img/landing/changelog-3.webp';
import Changelog4 from 'img/landing/changelog-4.webp';
import IntercomVideoThumbnail from 'img/landing/changelog-video-thumbnail.webp';
import ClickMe from 'img/landing/click-me.webp';
import FullSiteIcon from 'img/landing/demo-full-site-icon.webp';
import WidgetIcon from 'img/landing/demo-widget-icon.webp';
import Felix from 'img/landing/felix-small.webp';
import Jesse from 'img/landing/jesse.webp';
import Philippe from 'img/landing/philippe.webp';
import TryMe from 'img/landing/try-me.webp';

const VideoID = 'yimutj8ayu';

import 'css/components/www/_LandingFeaturesChildPage.scss';

class ChangelogLanding extends Component {
  static propTypes = {
    loadVideo: PropTypes.func,
    playVideo: PropTypes.func,
    trackEvent: PropTypes.func,
    viewer: PropTypes.shape({
      companies: PropTypes.array,
    }),
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Changelog Child Page');

    CannySDK.init();
    Canny('initChangelog', {
      appID: publicConfig('cannyCompanyID'),
      position: 'bottom',
      align: 'left',
    });
  }

  onLoadVideo = () => {
    const { loadVideo, trackEvent } = this.props;
    loadVideo(VideoID, (video) => {
      if (video.trackingEvents) {
        return;
      }
      video.trackingEvents = true;
      video.bind('play', () => {
        trackEvent('Started Changelog Video');
        return video.unbind;
      });
      video.bind('secondchange', (seconds) => {
        if (seconds !== 80) {
          return;
        }
        trackEvent('Finished Changelog Video');
        return video.unbind;
      });
    });
  };

  onPlayVideo = () => {
    this.onLoadVideo();

    const { playVideo } = this.props;
    playVideo(VideoID);
  };

  renderVideo() {
    return (
      <div className="playButtonContainer">
        <div className="playButton" />
        <div className="playButtonLabel">
          <UppercaseHeader>See how it works</UppercaseHeader>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="landingChangelogPage landingFeaturesChildPage">
        <LandingChangelogHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="text">
              <h1>Announce product updates with Canny's changelog&nbsp;software</h1>
              <p className="subheading">
                Increase feature awareness and drive user engagement with your newest&nbsp;features
              </p>
              <div className="links">
                <InstallExtensionButton
                  installButtonText="Get started"
                  installLink="https://developers.canny.io/install/changelog"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mainContentContainer">
          <Tappable onTap={this.onPlayVideo}>
            <div className="video" onMouseOver={this.onLoadVideo}>
              {this.renderVideo()}
              <LazyLoadedImage
                loading="eager"
                src={IntercomVideoThumbnail}
                alt="changelog video thumbnail"
              />
            </div>
          </Tappable>
          <div className="mainContentContainer">
            <div className="demos">
              <button className="demoButton" data-canny-changelog>
                <LazyLoadedImage className="buttonIcon" src={WidgetIcon} alt="widget icon" />
                <LazyLoadedImage className="tryMe" src={TryMe} alt="try me" />
                <div className="text">
                  <div className="title">Product widget</div>
                  <div className="description">
                    Embed directly into your product for easy&nbsp;notifications
                  </div>
                </div>
              </button>
              <a
                className="demoButton"
                href="https://feedback.canny.io/changelog"
                rel="noopener"
                target="_blank">
                <LazyLoadedImage className="buttonIcon" src={FullSiteIcon} alt="site icon" />
                <LazyLoadedImage className="clickMe" src={ClickMe} alt="click me" />
                <div className="text">
                  <div className="title">Full view</div>
                  <div className="description">
                    Your home for all previous product&nbsp;updates.
                  </div>
                </div>
              </a>
            </div>
            <h2>A product changelog takes you from product requests to new&nbsp;features</h2>
            <div className="point wide">
              <LazyLoadedImage src={Changelog1} alt="Embedded product changelog" />
              <div className="text">
                <h2 className="title">Increase new feature adoption and&nbsp;engagement</h2>
                <p>
                  Don’t let new features go unnoticed. Use your product changelog to show your
                  customers that your product is constantly improving. Publish detailed release
                  notes to help customers understand your&nbsp;features.
                </p>
              </div>
            </div>
            <div className="point wide">
              <LazyLoadedImage
                src={Changelog2}
                alt="In-app alert for users to review release notes in the changelog, closing the feedback loop."
              />
              <div className="text">
                <h2 className="title">Close the feedback&nbsp;loop</h2>
                <p>Transition seamlessly through:</p>
                <ul>
                  <li>User feedback management</li>
                  <li>Prioritization</li>
                  <li>Roadmapping</li>
                  <li>Launching in your official product changelog</li>
                </ul>
                <p>Plus, automatically keep your users updated as you make&nbsp;progress.</p>
              </div>
            </div>
            <div className="point wide">
              <LazyLoadedImage
                src={Changelog3}
                alt="Product changelog that is markdown supported"
              />
              <div className="text">
                <h2 className="title">Design changelog updates your&nbsp;way</h2>
                <p>
                  Stay true to your brand voice. Our changelog tool is flexible to suit your team’s
                  needs with markdown support and custom&nbsp;labels.
                </p>
              </div>
            </div>
            <div className="point wide">
              <LazyLoadedImage
                src={Changelog4}
                alt="Product changelog that is markdown supported"
              />
              <div className="text">
                <h2 className="title">
                  Handle release notes and changelog entries with one&nbsp;tool
                </h2>
                <p>
                  Canny is flexible and helps you create useful updates. Both changelog entries and
                  release notes benefit from good design and rich&nbsp;media.
                </p>
              </div>
            </div>
            <FeaturesChildPagePagination currentPage="changelog" />
            <LandingFooterCTA style="solid" expanded={true} />
            <Quotes title={<>Leading companies trust our release notes&nbsp;software</>}>
              <Quote
                header={
                  <>
                    Knak shares their software release notes with users as soon as they&nbsp;launch.
                  </>
                }
                text={
                  <>
                    Our users aren’t in the dark about what has been done and what hasn’t — they’re
                    immediately notified whenever we ship&nbsp;something.
                  </>
                }
                person={
                  <QuotePerson
                    name="Felix Higgs"
                    image={Felix}
                    title={<>Director of Implementation and Support,&nbsp;Knak</>}
                  />
                }
              />
              <Quote
                header={
                  <>
                    Missive re-engages inactive users with automatic product
                    update&nbsp;announcements
                  </>
                }
                text={
                  <>
                    Every time we release a big feature, we see people upgrade to a paid plan. It’s
                    the users who tried Missive in the past, went back to us via Canny, tried it
                    again, and then&nbsp;upgraded
                  </>
                }
                person={
                  <QuotePerson
                    name="Philippe Lehoux"
                    image={Philippe}
                    title={<>CEO,&nbsp;Missive</>}
                  />
                }
              />
              <Quote
                header={
                  <>
                    GiveButter boosts their NPS score by updating users on their
                    feature&nbsp;requests
                  </>
                }
                text={
                  <>
                    People always love it when something they’ve voted is built. In Q1, our NPS
                    score was 75, so this is a great indicator for us. Our customers know and tell
                    us that GiveButter really&nbsp;listens.
                  </>
                }
                person={
                  <QuotePerson
                    name="Jesse Sandala"
                    image={Jesse}
                    title={<>Director of Product,&nbsp;GiveButter</>}
                  />
                }
              />
            </Quotes>
            <FAQs
              faqs={[
                {
                  question: 'How much does it cost?',
                  answer:
                    "<p>Canny offers several plans to cover every team's needs. You can get started with our free changelog tools. If you need more powerful features and integrations, you can upgrade to a Growth or a Business plan.</p>",
                  html: (
                    <p>
                      Canny offers several plans to cover every team's needs. You can get started
                      with our free changelog tools. If you need more powerful features and
                      integrations, you can upgrade to a Growth or a Business plan.
                    </p>
                  ),
                },
                {
                  question: 'What else can I do with Canny?',
                  answer:
                    '<p>We offer a lot more than just a product changelog tool and release notes software. Canny has a suite of tools to help a product manager build better products. That includes feedback management solutions, prioritization tools, roadmapping tools, and a wide range of integrations to other product tools.</p>',
                  html: (
                    <p>
                      We offer a lot more than just a product changelog tool and release notes
                      software. Canny has a suite of tools to help a product manager build better
                      products. That includes feedback management solutions, prioritization tools,
                      roadmapping tools, and a wide range of integrations to other product tools.
                    </p>
                  ),
                },
                {
                  question: "What's the difference between a changelog update and a release note?",
                  answer:
                    "<div><p>The terms are often used interchangeably, but there are some differences.</p><p>They're different types of product updates and are usually written differently. Both describe changes or new features. Release notes are written for less technical audiences though. A changelog entry is more technical and often intended for developers and engineers. It ultimately comes down to what the purpose of your update is.</p><p>  Canny lets you write both types. It's like release notes management software  and a changelog tool in one.</p></div>",
                  html: (
                    <>
                      <p>
                        The terms are often used interchangeably, but there are some differences.
                      </p>
                      <p>
                        They're different types of product updates and are usually written
                        differently. Both describe changes or new features. Release notes are
                        written for less technical audiences though. A changelog entry is more
                        technical and often intended for developers and engineers. It ultimately
                        comes down to what the purpose of your update is.
                      </p>
                      <p>
                        Canny lets you write both types. It's like release notes management software
                        and a changelog tool in one.
                      </p>
                    </>
                  ),
                },
                {
                  question: 'Which teams benefit most from Canny?',
                  answer:
                    "<p>You can add unlimited contributors to your Canny account, so we encourage you to include anyone that can help in building your product. That includes letting customer success and sales record customer feedback. Product management can review feedback, prioritize features, and plan your roadmap. Finally, your software development team can learn more about customers' challenges when building features. Even marketers can learn about common pain points, and use the release notes tool in their campaigns.</p>",
                  html: (
                    <p>
                      You can add unlimited contributors to your Canny account, so we encourage you
                      to include anyone that can help in building your product. That includes
                      letting customer success and sales record customer feedback. Product
                      management can review feedback, prioritize features, and plan your roadmap.
                      Finally, your software development team can learn more about customers'
                      challenges when building features. Even marketers can learn about common pain
                      points, and use the release notes tool in their campaigns.
                    </p>
                  ),
                },
                {
                  question: 'Do you have any templates or examples?',
                  answer: `<p>Yes! Check out our blog for a list of great <a href="https://canny.io/blog/release-notes/" target="_blank" rel="noopener">release note templates and examples</a>.`,
                  html: (
                    <p>
                      Yes! Check out our blog for a list of great{' '}
                      <a href="https://canny.io/blog/release-notes/" target="_blank" rel="noopener">
                        release note templates and examples
                      </a>
                      .
                    </p>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withContexts({
  loadVideo: LoadVideoContext,
  playVideo: PlayVideoContext,
  trackEvent: TrackEventContext,
  viewer: ViewerContext,
})(ChangelogLanding);
